// Wraps the host history endpoint with the resolved range from the URL.
// The query key is intentionally rounded to the nearest minute so a
// re-render every 2s (from useFleetLive) doesn't bust the cache.

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FLEET_HISTORY_REFETCH_MS } from "./fleet-intervals";
import type { FleetRangeValue } from "./use-fleet-range";

interface CpuSlice {
	usrPct: number;
	nicePct: number;
	sysPct: number;
	iowaitPct: number;
	irqPct: number;
	softirqPct: number;
	stealPct: number;
	idlePct: number;
	totalPct: number;
}

interface CpuMetrics {
	aggregate: CpuSlice;
	perCore?: Array<CpuSlice & { cpu: number }>;
	source: "proc" | "os";
}

/** Raw /proc/meminfo KB values as stored in the FleetSnapshot.runtime JSON. */
interface MemMetrics {
	totalKB: number;
	freeKB: number;
	availableKB: number;
	buffersKB: number;
	cachedKB: number;
	sReclaimableKB: number;
	shmemKB: number;
	swapTotalKB: number;
	swapFreeKB: number;
	source: "proc" | "os";
}

export interface FleetDisplayInfo {
	display: number;
	pid: number;
	adopted: boolean;
	sessions: number;
}

export interface FleetDisplays {
	disabled: boolean;
	displays: FleetDisplayInfo[];
}

export interface RuntimeMetrics {
	activeSessions: number;
	sessionsToday: number;
	sessionsLast24h: number;
	recentLaunches: Array<{
		id: string;
		profile: string | null;
		status: string;
		startedAt: string;
		endedAt: string | null;
	}>;
	loadAvg1m: number;
	loadAvg5m?: number;
	loadAvg15m?: number;
	cpu?: CpuMetrics;
	mem?: MemMetrics;
	displays?: FleetDisplays;
	profileIndex: { count: number; ageSec: number | null };
}

export interface FleetHistorySnapshot {
	id: string;
	collectedAt: string;
	hostname: string;
	reachable: boolean;
	unreachableReason: "api-dead" | "host-down" | null;
	apiVersion: string | null;
	installedBuilds: Record<string, string[]> | null;
	installedProfiles: Record<string, string[]> | null;
	/** major -> OSes with profiles installed. Null on older workers. */
	installedProfileOses: Record<string, string[]> | null;
	latestBuilds: Record<string, string> | null;
	latestProfiles: Record<string, string> | null;
	cronLastRun: string | null;
	/** When browser/profile bundles were last synced into the worker's volume. */
	lastBundleSyncAt: string | null;
	uptime: number | null;
	diskFreeGB: number | null;
	diskTotalGB: number | null;
	runtime: RuntimeMetrics | null;
}

export interface FleetHistoryAggregates {
	samples: number;
	peakConcurrent: number;
	peakConcurrentAt: string | null;
	avgConcurrent: number;
	windowFrom: string;
	windowTo: string;
	earliestAvailable: string | null;
}

export interface FleetHistoryResponse {
	success: boolean;
	hostname: string;
	/**
	 * The true newest snapshot for this host, independent of the (possibly
	 * downsampled) `snapshots` series. Use this for summary cards so they
	 * always reflect the latest poll rather than a bucket representative.
	 */
	latest: FleetHistorySnapshot | null;
	snapshots: FleetHistorySnapshot[];
	aggregates: FleetHistoryAggregates;
}

interface QueryParams {
	from?: string;
	to?: string;
	range?: string;
	bucket: "auto";
}

function buildQuery(range: FleetRangeValue): QueryParams {
	if (range.kind === "preset") {
		return { range: range.preset, bucket: "auto" };
	}
	return {
		from: range.from.toISOString(),
		to: range.to.toISOString(),
		bucket: "auto",
	};
}

/**
 * `to`-rounding key: when the range is "live" (preset ending now), bucket
 * the cache key to the nearest minute so the snapshot poller's 30s tick
 * doesn't immediately refetch on every render.
 */
function keyTo(range: FleetRangeValue): string {
	if (!range.live) return range.to.toISOString();
	const minute = Math.floor(range.to.getTime() / 60_000) * 60_000;
	return new Date(minute).toISOString();
}

export function useFleetHostHistory(hostname: string, range: FleetRangeValue) {
	const params = buildQuery(range);
	return useQuery<FleetHistoryResponse>({
		// ["workers", hostname]: the same root the inventory list uses, so one
		// invalidation after a lifecycle action or an install refreshes both the
		// table and this worker's charts.
		queryKey: [
			"workers",
			hostname,
			range.kind,
			range.kind === "preset" ? range.preset : null,
			range.from.toISOString(),
			keyTo(range),
		],
		queryFn: async () => {
			const flat: Record<string, string> = { bucket: params.bucket };
			if (params.from) flat.from = params.from;
			if (params.to) flat.to = params.to;
			if (params.range) flat.range = params.range;
			const url = `/api/workers/${encodeURIComponent(hostname)}?${new URLSearchParams(flat).toString()}`;
			const res = await fetch(url, { credentials: "include" });
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			const json = (await res.json()) as
				| FleetHistoryResponse
				| { success: false; error?: { message?: string } };
			if (!("snapshots" in json) || !json.success) {
				const msg =
					"error" in json && json.error?.message
						? json.error.message
						: "Failed to load history";
				throw new Error(msg);
			}
			return json;
		},
		refetchInterval: range.live ? FLEET_HISTORY_REFETCH_MS : false,
		// Keep the prior chart/timeline on screen during a background refetch
		// or when the operator switches range presets (the key changes),
		// instead of flashing the empty state or a skeleton.
		placeholderData: keepPreviousData,
	});
}
